import {
  ConfigGroup,
  ConfigItem,
  ConfigItemType,
  ConfigItemValue,
  ConfigValue,
  DataTypeControlConfigValue,
  DateRangeConfigValue,
  NumericRangeConfigValue,
  SelectConfigValue,
  SelectConfigValueOption,
  formatContextSelectorLabel,
  isConfigGroup,
  isMultiSelect,
  isSelect,
} from '@ecosystem/newron-client-lib';

import { MultiEditInsight } from '../../../public/MultiEditRegister';

import { PngRendererData } from './PngRenderer';
import { contextSelectorData, marketInfo } from './PngRendererInfo';

export function formatMonthYear(date: Date): string {
  return new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    year: 'numeric',
  }).format(date);
}

const formatArrayValue = (key: string, values: any[]): string => {
  if (!values.length) {
    return '';
  }
  let formattedValue: string;
  if (key === ConfigKeys.Context) {
    formattedValue = values.map(v => v.label).join(', ');
  } else if (key === ConfigKeys.ContextSelector) {
    formattedValue = values.map(v => formatContextSelectorLabel(v)).join(', ');
  } else {
    formattedValue = values.map(v => v.label ?? getSelectLabelFromValue(key, v.value)).join(', ');
  }
  return formattedValue;
};

const formatDateRange = (range: DateRangeConfigValue): string =>
  `${range.min ? formatMonthYear(new Date(range.min)) : ''}-${range.max ? formatMonthYear(new Date(range.max)) : ''}`;

const formatNumericRange = (range: NumericRangeConfigValue): string => `${range.min ?? ''}-${range.max ?? ''}`;

const isNumericRange = (val: any): val is NumericRangeConfigValue =>
  val && (val.hasOwnProperty('min') || val.hasOwnProperty('max'));

const isSelectValue = (val: any): val is SelectConfigValue => val && val.hasOwnProperty('value');

export enum ConfigKeys {
  DataTypeControl = 'dataTypeControl',
  PageSettings = 'pageSettings',
  LazyLoadSelect = 'lazyLoadSelect',
  LazyLoadMultiSelect = 'lazyLoadMultiSelect',
  Context = 'context',
  Country = 'country',
  State = 'state',
  Age = 'age',
  FirstActivity = 'firstActivity',
  Market = 'Market',
  Brands = 'Brands',
  Retailers = 'Retailers',
  InnerFilterGroupOne = 'innerFilterGroupOne',
  InnerFilterOne = 'innerFilterOne',
  InnerFilterTwo = 'innerFilterTwo',
  InnerFilterGroupTwo = 'innerFilterGroupTwo',
  InnerFilterThree = 'innerFilterThree',
  InnerFilterFour = 'innerFilterFour',
  Group = 'group',
  GroupItem1 = 'groupItem1',
  GroupItem2 = 'groupItem2',
  ContextSelector = 'contextSelector',
}

export const PngRendererConfigItems: ConfigItem[] = [
  {
    key: ConfigKeys.PageSettings,
    label: 'Page settings',
    type: ConfigItemType.Group,
    value: [
      {
        key: ConfigKeys.DataTypeControl,
        type: ConfigItemType.DataTypeControl,
        value: 'static',
      },
      {
        key: ConfigKeys.Context,
        label: 'Context',
        type: ConfigItemType.MultiCell,
        options: marketInfo,
        tooltipInfo: 'Information about the context here',
      },
      {
        key: ConfigKeys.Country,
        label: 'Country',
        type: ConfigItemType.Select,
        options: [
          { value: 'ca', label: 'Canada' },
          { value: 'us', label: 'United States' },
        ],
        placeholder: 'Select a country',
        combobox: true,
        tooltipInfo: 'Information about the country here',
      },
      {
        key: ConfigKeys.LazyLoadMultiSelect,
        label: 'Lazy loading Multi Select',
        type: ConfigItemType.MultiSelect,
        options: [],
        isLoading: true,
      },
      {
        key: ConfigKeys.LazyLoadSelect,
        label: 'Lazy loading Select',
        type: ConfigItemType.Select,
        options: [],
        isLoading: true,
      },
      {
        key: ConfigKeys.State,
        label: 'State',
        type: ConfigItemType.Select,
        options: [
          { value: 'ny', label: 'New York' },
          { value: 'ca', label: 'California' },
        ],
        tooltipInfo: 'Information about the state here',
      },
      {
        key: ConfigKeys.Age,
        label: 'Age',
        type: ConfigItemType.NumericRange,
        options: { min: 0, max: 100 },
        tooltipInfo: 'Information about the age range here',
      },
      {
        key: ConfigKeys.FirstActivity,
        label: 'First activity',
        type: ConfigItemType.DateRange,
        options: { min: new Date(2021, 1, 1), max: new Date(2024, 10, 1) },
        tooltipInfo: 'Information about the first activity here',
      },
    ],
  },
  {
    key: ConfigKeys.ContextSelector,
    label: 'Context selector',
    type: ConfigItemType.ContextSelector,
    options: contextSelectorData,
    tooltipInfo: 'Information about the context selector here',
    value: [],
  },
  {
    key: ConfigKeys.Market,
    label: 'Market',
    type: ConfigItemType.Group,
    value: [
      {
        key: ConfigKeys.Brands,
        label: 'Brands',
        type: ConfigItemType.MultiSelect,
        options: new Array(10).fill(0).map((_, idx) => ({ value: 'option' + idx, label: 'Option ' + idx })),
        combobox: true,
        showSelectAllOptions: true,
      },
      {
        key: ConfigKeys.Retailers,
        label: 'Retailers',
        type: ConfigItemType.MultiSelect,
        options: new Array(3).fill(0).map((_, idx) => ({ value: 'option' + idx, label: 'Option ' + idx })),
        tooltipInfo: 'Information about the retailers here',
      },
      {
        key: ConfigKeys.InnerFilterGroupOne,
        label: 'Inner Filter Group',
        type: ConfigItemType.Group,
        value: [
          {
            key: ConfigKeys.InnerFilterOne,
            label: 'Inner Filter',
            type: ConfigItemType.MultiSelect,
            options: new Array(5)
              .fill(0)
              .map((_, idx) => ({ value: 'inner-option' + idx, label: 'Inner Option ' + idx })),
            combobox: true,
            tooltipInfo: 'Information about the inner filter here',
          },
          {
            key: ConfigKeys.InnerFilterTwo,
            label: 'Inner Filter 2',
            type: ConfigItemType.MultiSelect,
            options: new Array(2)
              .fill(0)
              .map((_, idx) => ({ value: 'inner-option' + idx, label: 'Inner Option ' + idx })),
            tooltipInfo: 'Information about the inner filter 2 here',
          },
        ],
      },
      {
        key: ConfigKeys.InnerFilterGroupTwo,
        label: 'Inner Filter Group',
        type: ConfigItemType.Group,
        value: [
          {
            key: ConfigKeys.InnerFilterThree,
            label: 'Inner Filter',
            type: ConfigItemType.MultiSelect,
            options: new Array(5)
              .fill(0)
              .map((_, idx) => ({ value: 'inner-option' + idx, label: 'Inner Option ' + idx })),
            tooltipInfo: 'Information about the inner filter here',
          },
          {
            key: ConfigKeys.InnerFilterFour,
            label: 'Inner Filter 2',
            type: ConfigItemType.MultiSelect,
            options: new Array(2)
              .fill(0)
              .map((_, idx) => ({ value: 'inner-option' + idx, label: 'Inner Option ' + idx })),
            tooltipInfo: 'Information about the inner filter 2 here',
          },
        ],
      },
    ],
  },
  {
    key: ConfigKeys.Group,
    label: 'Group',
    type: ConfigItemType.Group,
    value: [
      {
        key: ConfigKeys.GroupItem1,
        label: 'Group Item 1',
        type: ConfigItemType.Select,
        options: [
          { value: 'group1-option1', label: 'Group 1 Option 1' },
          { value: 'group1-option2', label: 'Group 1 Option 2' },
        ],
        tooltipInfo: 'Information about group item 1 here',
      },
      {
        key: ConfigKeys.GroupItem2,
        label: 'Group Item 2',
        type: ConfigItemType.Select,
        options: [
          { value: 'group2-option1', label: 'Group 2 Option 1' },
          { value: 'group2-option2', label: 'Group 2 Option 2' },
        ],
        tooltipInfo: 'Information about group item 2 here',
      },
    ],
  },
];

export const getConfigValueFromConfigItem = (configItem: ConfigItem): ConfigItemValue[] => {
  const getSingleConfigValue = (value: ConfigValue): ConfigItemValue => ({ key: configItem.key, value });

  return isConfigGroup(configItem)
    ? (configItem.value || []).flatMap(getConfigValueFromConfigItem)
    : [getSingleConfigValue(configItem.value)];
};

export const getDataTypeFromConfigValues = (
  configValues: ConfigItemValue[]
): DataTypeControlConfigValue | undefined => {
  const dataTypeItem = configValues.find(item => item.key === ConfigKeys.DataTypeControl)?.value;
  return dataTypeItem as DataTypeControlConfigValue;
};

// conditionally show/hide config items based on other config values
const ruleBook = [
  {
    key: ConfigKeys.Country,
    show: true,
  },
  {
    key: ConfigKeys.DataTypeControl,
    show: (configValues: ConfigItemValue[]) => {
      return configValues?.some(configValue => configValue.key === ConfigKeys.DataTypeControl);
    },
  },
  {
    key: ConfigKeys.State,
    show: (configValues: ConfigItemValue[]) => {
      const country = findNestedConfigItem(
        ConfigKeys.Country,
        updateConfigItemsValues(PngRendererConfigItems, configValues)
      );
      return (country?.value as SelectConfigValue)?.value === 'us';
    },
  },
  {
    key: ConfigKeys.Retailers,
    show: (configValues: ConfigItemValue[]) => {
      const filter = findNestedConfigItem(
        ConfigKeys.Brands,
        updateConfigItemsValues(PngRendererConfigItems, configValues)
      );
      return (filter?.value as SelectConfigValue[])?.length > 5;
    },
  },
];

export const applyRuleBook = (configItems: ConfigItem[], configValues: ConfigItemValue[]): ConfigItem[] => {
  return configItems
    .map(configItem => {
      if (isConfigGroup(configItem)) {
        return {
          ...configItem,
          value: applyRuleBook(configItem.value, configValues),
        };
      }
      const rule = ruleBook.find(rule => rule.key === configItem.key);
      if (!rule) {
        return configItem;
      }

      const shouldShow = typeof rule.show === 'function' ? rule.show(configValues) : rule.show;
      return shouldShow && configItem;
    })
    .filter(Boolean);
};

export const updateConfigItemsValues = (configItems: ConfigItem[], configValues: ConfigItemValue[]): ConfigItem[] => {
  return configItems.map(configItem => {
    const configValue = configValues?.find(filter => filter.key === configItem.key);
    switch (configItem.type) {
      case ConfigItemType.MultiSelect:
      case ConfigItemType.Select:
      case ConfigItemType.NumericRange:
      case ConfigItemType.MultiCell:
      case ConfigItemType.DataTypeControl:
      case ConfigItemType.ContextSelector:
        return configValue?.value ? { ...configItem, value: configValue?.value } : configItem;
      case ConfigItemType.DateRange: {
        const value = configValue?.value as DateRangeConfigValue;
        return value
          ? {
              ...configItem,
              value: { min: value.min ? new Date(value.min) : null, max: value.max ? new Date(value.max) : null },
            }
          : configItem;
      }
      case ConfigItemType.Group:
        return {
          ...configItem,
          value: updateConfigItemsValues(configItem.value, configValues),
        };
      default:
        return configItem;
    }
  }) as ConfigItem[];
};

export const findNestedConfigItem = (key: string, configItems = PngRendererConfigItems): ConfigItem => {
  for (const configItem of configItems) {
    if (configItem.key === key) {
      return configItem;
    }
    if (Array.isArray(configItem.value) && configItem.value.length > 0 && configItem.value[0]?.hasOwnProperty('key')) {
      const nestedConfigItem = findNestedConfigItem(key, (configItem as ConfigGroup).value);
      if (nestedConfigItem) {
        return nestedConfigItem;
      }
    }
  }
};

export const patchConfigItem = (updatedItem: ConfigItem, configItems = PngRendererConfigItems): ConfigItem[] => {
  return configItems.map(configItem => {
    if (configItem.key === updatedItem.key) {
      return updatedItem;
    }
    if (isConfigGroup(configItem)) {
      return {
        ...configItem,
        value: patchConfigItem(updatedItem, configItem.value),
      };
    }
    return configItem;
  });
};

export const patchConfigItems = (updatedItems: ConfigItem[], configItems = PngRendererConfigItems): ConfigItem[] => {
  return configItems.map(configItem => {
    const match = findNestedConfigItem(configItem.key, updatedItems) ?? {};
    if (isConfigGroup(configItem)) {
      return {
        ...configItem,
        ...match,
        value: patchConfigItems(updatedItems, configItem.value),
      };
    }
    return {
      ...configItem,
      ...match,
    };
  });
};

export const fillMissingOptions = (configItems: ConfigItem[]): ConfigItem[] => {
  return configItems.map(configItem => {
    if (isConfigGroup(configItem)) {
      return {
        ...configItem,
        value: fillMissingOptions(configItem.value),
      };
    }
    if (
      (isSelect(configItem) || isMultiSelect(configItem)) &&
      (configItem.key === ConfigKeys.LazyLoadSelect || configItem.key === ConfigKeys.LazyLoadMultiSelect) &&
      !configItem.options.length &&
      configItem.value
    ) {
      return {
        ...configItem,
        options: (Array.isArray(configItem.value) ? configItem.value : [configItem.value]) as SelectConfigValueOption[],
      };
    }
    return configItem;
  });
};

export const getSelectLabelFromValue = (key: string, value: string): string => {
  const configItem = findNestedConfigItem(key);
  if (configItem?.type === ConfigItemType.MultiSelect) {
    return configItem?.options
      .filter(option => value === option.value)
      .map(option => option.label)
      .join(', ');
  } else if (configItem?.type === ConfigItemType.Select) {
    return configItem?.options.find(option => option.value === value)?.label || '';
  }
};

export const getValueFromConfigValue = (configItemValue: ConfigItemValue): string => {
  const { key, value: configValue } = configItemValue;

  if (!configValue) {
    return '';
  }

  if (Array.isArray(configValue)) {
    return formatArrayValue(key, configValue);
  }

  if (key === ConfigKeys.FirstActivity) {
    return formatDateRange(configValue as DateRangeConfigValue);
  }

  if (isNumericRange(configValue)) {
    return formatNumericRange(configValue as NumericRangeConfigValue);
  }

  if (isSelectValue(configValue)) {
    return configValue.label ?? getSelectLabelFromValue(key, configValue.value);
  }

  return '';
};

export const getAppliedValues = (
  insights: MultiEditInsight<PngRendererData>[],
  key: ConfigKeys
): Record<string, ConfigValue> => {
  return insights.reduce(
    (acc, insight) => {
      const configValue = insight.data.configValues?.find(configValue => configValue.key === key)?.value;

      if (configValue) {
        acc[insight.id] = configValue;
      }

      return acc;
    },
    {} as Record<string, ConfigValue>
  );
};
